/**
 * Renders one WhatsApp message body according to its `messageType`.
 *
 * Before inbound capture existed every message was a text bubble, and the
 * transcript printed `msg.content` verbatim. Non-text messages now carry a
 * synthesised placeholder in `content` (kept as a fallback for list previews
 * and search), but the transcript should render the STRUCTURED payload
 * instead — a location with its coordinates, a contact with its phone number,
 * an image with its actual thumbnail.
 *
 * Deliberately icon-based rather than emoji: emoji render inconsistently
 * across platforms, carry a casual register that reads wrong in an operator
 * console, and cannot inherit theme colour or type scale. Every affordance
 * here is a real icon on a muted surface. The one exception is a reaction
 * badge, where the emoji IS the customer's literal content.
 */
import { useState, type ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Typography,
  Stack,
  Link,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  PlaceOutlined,
  PersonOutline,
  DescriptionOutlined,
  MicNone,
  VideocamOutlined,
  ImageOutlined,
  ShoppingCartOutlined,
  InfoOutlined,
  ErrorOutlineOutlined,
  PhoneOutlined,
  MailOutline,
  OpenInNew,
  BrokenImageOutlined,
  LockOutlined,
  FormatListBulletedOutlined,
  SmartButtonOutlined,
  AutoStoriesOutlined,
  ReplyOutlined,
  DoNotDisturbOnOutlined,
} from '@mui/icons-material';
import MediaPreviewDialog, { type MediaPreviewKind } from '@/components/common/MediaPreviewDialog';

export interface MessageAttachment {
  type?: string;
  s3Url?: string;
  thumbUrl?: string;
  mimeType?: string;
  size?: number;
  storageState?: 'pending' | 'stored' | 'expired' | 'skipped_quota' | 'failed' | 'deleted';
  width?: number;
  height?: number;
  error?: string;
  /**
   * The SERVER's verdict on whether this attachment may be displayed —
   * `!isRenderableAttachment(att, tenantIsOverQuota)`, stamped on every read
   * path and pushed on the live socket event.
   *
   * This used to be inferred client-side as "it's a quota capture and it has no
   * thumbnail". That inference was wrong for every type we cannot thumbnail:
   * video, voice notes and documents NEVER have a thumbUrl, so they were
   * locked permanently — freeing space could not clear a signal that never
   * changes. Hence an explicit flag from the only party that knows.
   */
  quotaLocked?: boolean;
  /** Over-quota capture: the size of the original that was never stored, i.e.
   *  what freeing space would recover. `size` counts bytes actually held. */
  skippedBytes?: number;
}

export interface MessageBodyProps {
  messageType?: string | undefined;
  content: string;
  attachments?: MessageAttachment[] | undefined;
  location?: { latitude?: number; longitude?: number; name?: string; address?: string } | undefined;
  contacts?:
    | Array<{
        formattedName?: string;
        phones?: Array<{ phone?: string; type?: string; waId?: string }>;
        emails?: Array<{ email?: string; type?: string }>;
        org?: { company?: string; title?: string };
      }>
    | undefined;
  order?:
    | {
        catalogId?: string;
        text?: string;
        productItems?: Array<{
          productRetailerId?: string;
          quantity?: number;
          itemPrice?: number;
          currency?: string;
        }>;
      }
    | undefined;
  system?: { type?: string; body?: string; waId?: string } | undefined;
  /** Outbound native List / Button message — a SNAPSHOT of what was actually
   *  sent (see WhatsAppMessage.model.ts on the backend for why it's stored
   *  resolved rather than joined back to the saved library record). */
  interactive?:
    | {
        kind: 'list' | 'button';
        bodyText?: string;
        headerText?: string;
        footerText?: string;
        buttonText?: string;
        sections?: Array<{
          title?: string;
          rows: Array<{ id?: string; title: string; description?: string }>;
        }>;
        buttons?: Array<{ id?: string; title: string }>;
        header?: { type?: string; text?: string; filename?: string };
      }
    | undefined;
  providerErrors?: Array<{ code?: number; title?: string; message?: string }> | undefined;
  /**
   * Instagram-only (Phase 2.3 event types). Present when this message is a
   * reply to a story the business posted (`replyToMid`) or a mention of the
   * business in someone else's story (`storyUrl`/`storyId`, no `replyToMid`)
   * — VERIFIED shape, mirrors `message.reply_to` on Meta's own webhook.
   * Found-and-fixed 2026-09-28: the backend always computed this but never
   * persisted it until this same pass, so it reached zero UI before now.
   */
  storyContext?: { replyToMid?: string; storyUrl?: string; storyId?: string } | undefined;
  /** Instagram-only: the customer tapped a Quick Reply the business sent
   *  earlier — the developer-defined payload string, same role as WhatsApp's
   *  template quick-reply `payload`. */
  quickReplyPayload?: string | undefined;
  /** Instagram-only: Meta itself could not classify/deliver the message
   *  content (`message.is_unsupported`) — distinct from a LOCAL normalizer
   *  failure, which already renders via the existing 'unsupported'
   *  messageType case below. */
  isUnsupported?: boolean | undefined;
  /** Renders caption/body text. Supplied by the parent so typography stays
   *  identical to a plain text bubble. */
  renderText: (text: string) => ReactNode;
}

const SURFACE = 'rgba(15, 23, 42, 0.04)';
const BORDER = 'rgba(15, 23, 42, 0.10)';

/** Shared frame for every non-text body, so they read as one family. */
function Panel({
  icon,
  children,
  dense,
}: {
  icon?: ReactNode;
  children: ReactNode;
  dense?: boolean;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        alignItems: 'flex-start',
        bgcolor: SURFACE,
        border: `1px solid ${BORDER}`,
        borderRadius: 1.5,
        px: 1.25,
        py: dense ? 0.75 : 1,
        minWidth: 190,
      }}
    >
      {icon && (
        <Box sx={{ display: 'flex', color: 'text.secondary', mt: '1px', flexShrink: 0 }}>
          {icon}
        </Box>
      )}
      <Box sx={{ minWidth: 0, flex: 1 }}>{children}</Box>
    </Box>
  );
}

function formatBytes(n?: number): string {
  if (!n || n <= 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

const MEDIA_ICON: Record<string, ReactNode> = {
  image: <ImageOutlined fontSize="small" />,
  sticker: <ImageOutlined fontSize="small" />,
  video: <VideocamOutlined fontSize="small" />,
  audio: <MicNone fontSize="small" />,
  document: <DescriptionOutlined fontSize="small" />,
};

const MEDIA_LABEL: Record<string, string> = {
  image: 'Photo',
  sticker: 'Sticker',
  video: 'Video',
  audio: 'Voice message',
  document: 'Document',
};

/**
 * Outbound media-library sends store `[File: <name>]` as the message text when
 * the sender added no caption. That is the right thing for the conversation
 * LIST, where "which file?" is the only question a one-line preview can answer
 * — but inside the bubble it is not a caption, it is the file's NAME.
 *
 * So it is lifted out and used to title the media panel (replacing the generic
 * "Document"/"Video" label, which is strictly less informative), and an image —
 * which shows itself and needs no title — drops it entirely rather than
 * printing "[File: baatly logo]" under the picture.
 */
const FILE_PLACEHOLDER_RE = /^\[File:\s*(.+?)\s*\]$/;

/** Placeholder strings the normalizer synthesises when a media message has no
 *  caption. If `content` is one of these there is no real caption to show. */
const SYNTHESISED = new Set([
  'Photo',
  'Video',
  'Audio',
  'Voice message',
  'Sticker',
  'Document',
  'Attachment',
  'Location',
  'Contact',
]);

function isSynthesised(content: string): boolean {
  // The normalizer prefixes placeholders with a pictograph; strip any leading
  // non-letter characters before comparing.
  const bare = content.replace(/^[^\p{L}]+/u, '').trim();
  return SYNTHESISED.has(bare) || bare.startsWith('Order ·') || bare === '';
}

/** Status line for media whose bytes are not (yet) available. */
function MediaStatus({ att }: { att: MessageAttachment }) {
  const state = att.storageState;
  if (state === 'pending') {
    // A queued download that has hit a non-transient problem (most often a
    // rejected WhatsApp token) carries the reason. Still pending — it resumes
    // once the cause is fixed — but a bare "Downloading" spinner for the hours
    // the retry budget covers is not an honest thing to show.
    return (
      <Stack direction="row" spacing={0.75} alignItems="center">
        {!att.error && <CircularProgress size={11} thickness={5} sx={{ color: 'text.disabled' }} />}
        <Typography
          variant="caption"
          sx={{ color: att.error ? 'warning.dark' : 'text.disabled', fontSize: '0.68rem' }}
        >
          {att.error || 'Downloading'}
        </Typography>
      </Stack>
    );
  }
  // `skipped_quota` WITH a preview is deliberately absent: once the thumbnail
  // is visible again the bubble should read as a normal image, and the missing
  // original is not something the viewer can act on. But a quota capture with
  // no bytes at all (video/audio/document — nothing we can thumbnail) has
  // literally nothing to show, and staying silent there renders a bare type
  // label that reads like an unexplained failure. Say what happened instead;
  // recovery re-fetches it in the background once there is room.
  // Returning '' below short-circuits to null, so no empty node or its
  // surrounding gap is rendered.
  const msg =
    state === 'expired'
      ? 'No longer available from WhatsApp'
      : state === 'deleted'
        ? 'Removed'
        : state === 'failed'
          ? 'Could not be downloaded'
          : state === 'skipped_quota' && !att.thumbUrl && !att.s3Url
            ? 'storage full'
            : '';
  if (!msg) return null;
  return (
    <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
      {msg}
    </Typography>
  );
}

/**
 * Over-quota capture: a deliberate, self-explanatory locked tile at the same
 * footprint as a real preview. Not a broken image and not silence — the tenant
 * should understand exactly why it is hidden and what to do about it.
 *
 */
function QuotaLockedTile({
  label,
  skippedBytes,
  width,
  height,
  isVisual,
}: {
  label: string;
  skippedBytes?: number | undefined;
  width?: number | undefined;
  height?: number | undefined;
  isVisual: boolean;
}) {
  // The app serves these routes BOTH under /admin and bare (see AdminLayout's
  // pathPrefix), so a hardcoded prefix would throw the user out of the URL
  // space they are currently in.
  const prefix = window.location.pathname.startsWith('/admin') ? '/admin' : '';
  const ratio = width && height ? height / width : 0.66;
  return (
    <Box
      sx={{
        position: 'relative',
        width: 220,
        ...(isVisual
          ? { paddingTop: `${Math.min(Math.max(ratio, 0.4), 1.2) * 100}%` }
          : { py: 2.5 }),
        borderRadius: 1.5,
        border: `1px dashed ${BORDER}`,
        bgcolor: SURFACE,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          ...(isVisual ? { position: 'absolute', inset: 0 } : {}),
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.75,
          px: 1.5,
          textAlign: 'center',
        }}
      >
        <LockOutlined sx={{ fontSize: 20, color: 'text.disabled' }} />
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600 }}
        >
          {label} storage full
        </Typography>
        {skippedBytes ? (
          <Typography
            variant="caption"
            sx={{ color: 'text.disabled', fontSize: '0.65rem', lineHeight: 1.2 }}
          >
            {formatBytes(skippedBytes)}
          </Typography>
        ) : null}
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontSize: '0.65rem', lineHeight: 1.35 }}
        >
          Either{' '}
          <Link
            component={RouterLink}
            to={`${prefix}/billing/active-plan?tab=compare`}
            underline="hover"
            onClick={(e) => e.stopPropagation()}
            sx={{ fontWeight: 600 }}
          >
            Update plan
          </Link>{' '}
          or{' '}
          <Link
            component={RouterLink}
            to={`${prefix}/activity/media`}
            underline="hover"
            onClick={(e) => e.stopPropagation()}
            sx={{ fontWeight: 600 }}
          >
            Free up space
          </Link>{' '}
          to view it.
        </Typography>
      </Box>
    </Box>
  );
}

type MediaBodyProps = {
  messageType: string;
  attachments?: MessageAttachment[] | undefined;
  content: string;
  renderText: MessageBodyProps['renderText'];
};

function MediaBody(props: MediaBodyProps) {
  const { messageType, attachments, content, renderText } = props;
  // An Instagram "album" (several photos/videos in one message) renders each
  // item through the SAME single-attachment renderer WhatsApp uses — so
  // loading, quota-lock, broken and preview states are all shared, never
  // re-implemented. The caption, if any, is printed once under the stack.
  if (attachments && attachments.length > 1) {
    return (
      <Stack spacing={0.75}>
        {attachments.map((a, i) => (
          <SingleMediaBody
            key={i}
            messageType={a.type || messageType}
            attachments={[a]}
            content=""
            renderText={renderText}
          />
        ))}
        {content && !isSynthesised(content) && renderText(content)}
      </Stack>
    );
  }
  return <SingleMediaBody {...props} />;
}

function SingleMediaBody({ messageType, attachments, content, renderText }: MediaBodyProps) {
  // Owned here rather than lifted to the transcript: a bubble is the only
  // thing that knows which attachment it is showing, and every caller of
  // MessageBody would otherwise have to thread the same state through.
  const [previewOpen, setPreviewOpen] = useState(false);
  const att = attachments?.[0];
  // A file we sent carries its own name in `content`; see FILE_PLACEHOLDER_RE.
  // It titles the panel instead of being printed as a caption.
  const fileName = FILE_PLACEHOLDER_RE.exec(String(content || '').trim())?.[1];
  const label = fileName || MEDIA_LABEL[messageType] || 'Attachment';
  const caption = content && !fileName && !isSynthesised(content) ? content : undefined;
  const isImage = messageType === 'image' || messageType === 'sticker';
  // Over-quota capture: the file WAS received and the preview WAS kept, but a
  // full store must feel full, so nothing is displayed until space is freed.
  // The server already withholds the URLs; this makes the intent explicit here
  // rather than relying on a missing field, and keeps the bubble from falling
  // back to a bare label that reads like a failure.
  const isVisual = isImage || messageType === 'video';
  // Over-quota capture: received, but the ORIGINAL was never stored. The server
  // is the authority on whether it may be shown and says so explicitly via
  // `quotaLocked` — see the field's doc comment for why a missing thumbUrl is
  // NOT a usable substitute (video/audio/document never have one, so inferring
  // from it pinned them to this tile forever).
  //
  // Strictly the server's flag — never re-derived here. Against a server that
  // predates it the tile simply does not lock, which degrades to showing the
  // normal (empty) media panel rather than to locking media that should be
  // visible.
  const quotaLocked = att?.quotaLocked === true;
  const preview = quotaLocked ? undefined : att?.thumbUrl || (isImage ? att?.s3Url : undefined);
  const href = quotaLocked ? undefined : att?.s3Url || att?.thumbUrl;
  const isDownloading = att?.storageState === 'pending';

  // Over-quota: the shared locked tile (see QuotaLockedTile) — the identical
  // treatment each cell of an album grid shows, so the two renderers cannot
  // drift into explaining the same server verdict differently.
  if (quotaLocked) {
    return (
      <Stack spacing={0.75}>
        <QuotaLockedTile
          label={label}
          skippedBytes={att?.skippedBytes}
          width={att?.width}
          height={att?.height}
          isVisual={isVisual}
        />
        {caption && renderText(caption)}
      </Stack>
    );
  }

  // Visual placeholder while the bytes are still being fetched. Reserved at the
  // ORIGINAL aspect ratio when Meta told us the dimensions, so the transcript
  // does not jump when the real thumbnail swaps in.
  if (isVisual && !preview && isDownloading) {
    const ratio = att?.width && att?.height ? att.height / att.width : 0.66;
    return (
      <Stack spacing={0.75}>
        <Box
          sx={{
            position: 'relative',
            width: 220,
            paddingTop: `${Math.min(Math.max(ratio, 0.4), 1.2) * 100}%`,
            borderRadius: 1.5,
            border: `1px solid ${BORDER}`,
            bgcolor: SURFACE,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              // Subtle sweep so it reads as "working", not "broken" — dropped
              // once we know it is NOT working, since animating progress that
              // has stopped is the misleading part.
              background:
                'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.55) 50%, transparent 100%)',
              backgroundSize: '200% 100%',
              animation: att?.error ? 'none' : 'wa-media-shimmer 1.4s ease-in-out infinite',
              '@keyframes wa-media-shimmer': {
                '0%': { backgroundPosition: '200% 0' },
                '100%': { backgroundPosition: '-200% 0' },
              },
              '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
            }}
          >
            {isImage ? (
              <ImageOutlined sx={{ fontSize: 22, color: 'text.disabled' }} />
            ) : (
              <VideocamOutlined sx={{ fontSize: 22, color: 'text.disabled' }} />
            )}
            {/* Same rule as MediaStatus: a stalled download states its reason
                rather than animating as if progress were being made. */}
            {att?.error ? (
              <Typography
                variant="caption"
                sx={{
                  color: 'warning.dark',
                  fontSize: '0.66rem',
                  px: 1.5,
                  textAlign: 'center',
                  lineHeight: 1.35,
                }}
              >
                {att.error}
              </Typography>
            ) : (
              <Stack direction="row" spacing={0.75} alignItems="center">
                <CircularProgress size={11} thickness={5} sx={{ color: 'text.disabled' }} />
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                  Receiving {label.toLowerCase()}
                </Typography>
              </Stack>
            )}
          </Box>
        </Box>
        {caption && renderText(caption)}
      </Stack>
    );
  }

  // `document` is deliberately NOT forced: the dialog's document kind means
  // "render as PDF", and a .docx attachment must fall through to its honest
  // "no preview" tile instead of an empty frame.
  const previewKind: MediaPreviewKind | undefined = isImage
    ? 'image'
    : messageType === 'video'
      ? 'video'
      : messageType === 'audio'
        ? 'audio'
        : undefined;

  return (
    <Stack spacing={0.75}>
      {isImage && preview ? (
        <Box
          component={href ? 'button' : 'div'}
          {...(href
            ? {
                type: 'button' as const,
                onClick: () => setPreviewOpen(true),
                'aria-label': `Preview ${caption || label}`,
              }
            : {})}
          sx={{
            display: 'block',
            p: 0,
            bgcolor: 'transparent',
            borderRadius: 1.5,
            overflow: 'hidden',
            border: `1px solid ${BORDER}`,
            lineHeight: 0,
            maxWidth: 260,
            cursor: href ? 'pointer' : 'default',
          }}
        >
          <Box
            component="img"
            src={preview}
            alt={caption || label}
            loading="lazy"
            sx={{
              display: 'block',
              width: '100%',
              height: 'auto',
              maxHeight: 280,
              objectFit: 'cover',
            }}
          />
        </Box>
      ) : (
        <Panel
          icon={
            att?.storageState === 'expired' ? (
              <BrokenImageOutlined fontSize="small" />
            ) : (
              MEDIA_ICON[messageType]
            )
          }
        >
          <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }} noWrap>
                {label}
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                {/* An over-quota capture holds no bytes, so `size` is 0 — but
                    the size of the original we refused IS known, and it is the
                    number that makes "storage was full" make sense. */}
                {att?.size || att?.skippedBytes ? (
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.disabled', fontSize: '0.68rem' }}
                  >
                    {formatBytes(att.size || att.skippedBytes)}
                  </Typography>
                ) : null}
                <MediaStatus att={att || {}} />
              </Stack>
            </Box>
            {href && (
              <Tooltip title="Preview">
                <IconButton
                  size="small"
                  onClick={() => setPreviewOpen(true)}
                  sx={{ color: 'primary.main', flexShrink: 0, p: 0.5 }}
                  aria-label={`Preview ${label}`}
                >
                  <OpenInNew sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Panel>
      )}
      {caption && renderText(caption)}
      {isImage && preview && <MediaStatus att={att || {}} />}
      {href && (
        <MediaPreviewDialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          url={href}
          fileName={label}
          {...(previewKind ? { kind: previewKind } : {})}
          {...(att?.mimeType ? { mimeType: att.mimeType } : {})}
          {...(caption ? { caption } : {})}
        />
      )}
    </Stack>
  );
}

export default function MessageBody(props: MessageBodyProps) {
  const { messageType, content, renderText } = props;

  switch (messageType) {
    case 'image':
    case 'sticker':
    case 'video':
    case 'audio':
    case 'document':
      return (
        <MediaBody
          messageType={messageType}
          attachments={props.attachments}
          content={content}
          renderText={renderText}
        />
      );

    case 'location': {
      const l = props.location || {};
      const hasCoords = typeof l.latitude === 'number' && typeof l.longitude === 'number';
      const maps = hasCoords
        ? `https://www.google.com/maps/search/?api=1&query=${l.latitude},${l.longitude}`
        : undefined;
      return (
        <Panel icon={<PlaceOutlined fontSize="small" />}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
            {l.name || 'Shared location'}
          </Typography>
          {l.address && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', fontSize: '0.72rem' }}
            >
              {l.address}
            </Typography>
          )}
          {hasCoords && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.disabled',
                fontSize: '0.68rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {l.latitude!.toFixed(5)}, {l.longitude!.toFixed(5)}
            </Typography>
          )}
          {maps && (
            <Link
              href={maps}
              target="_blank"
              rel="noopener noreferrer"
              variant="caption"
              sx={{
                fontSize: '0.72rem',
                mt: 0.25,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
              }}
            >
              Open in Maps <OpenInNew sx={{ fontSize: 12 }} />
            </Link>
          )}
        </Panel>
      );
    }

    case 'contacts': {
      const contacts = props.contacts || [];
      if (contacts.length === 0) return <>{renderText(content)}</>;
      return (
        <Stack spacing={0.75}>
          {contacts.map((c, i) => {
            const phone = c.phones?.[0]?.phone;
            const email = c.emails?.[0]?.email;
            const extraPhones = (c.phones?.length || 0) - 1;
            return (
              <Panel key={i} icon={<PersonOutline fontSize="small" />}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
                  {c.formattedName || phone || 'Contact'}
                </Typography>
                {(c.org?.title || c.org?.company) && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', fontSize: '0.72rem' }}
                  >
                    {[c.org?.title, c.org?.company].filter(Boolean).join(' · ')}
                  </Typography>
                )}
                {phone && (
                  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.25 }}>
                    <PhoneOutlined sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Link
                      href={`tel:${phone}`}
                      variant="caption"
                      sx={{ fontSize: '0.74rem', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {phone}
                    </Link>
                    {c.phones?.[0]?.type && (
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.disabled', fontSize: '0.66rem' }}
                      >
                        {c.phones[0].type}
                      </Typography>
                    )}
                  </Stack>
                )}
                {email && (
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <MailOutline sx={{ fontSize: 13, color: 'text.disabled' }} />
                    <Link href={`mailto:${email}`} variant="caption" sx={{ fontSize: '0.74rem' }}>
                      {email}
                    </Link>
                  </Stack>
                )}
                {extraPhones > 0 && (
                  <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.disabled', fontSize: '0.66rem' }}
                  >
                    +{extraPhones} more number{extraPhones === 1 ? '' : 's'}
                  </Typography>
                )}
              </Panel>
            );
          })}
        </Stack>
      );
    }

    case 'order': {
      const o = props.order || {};
      const items = o.productItems || [];
      const currency = items[0]?.currency || '';
      const total = items.reduce((sum, it) => sum + (it.itemPrice || 0) * (it.quantity || 0), 0);
      return (
        <Panel icon={<ShoppingCartOutlined fontSize="small" />}>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.82rem' }}>
            Catalog order · {items.length} item{items.length === 1 ? '' : 's'}
          </Typography>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {items.slice(0, 6).map((it, i) => (
              <Stack key={i} direction="row" justifyContent="space-between" spacing={1}>
                <Typography
                  variant="caption"
                  sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
                  noWrap
                >
                  {it.quantity}
                  {'× '}
                  {it.productRetailerId}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: '0.72rem',
                    color: 'text.secondary',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {it.currency} {((it.itemPrice || 0) * (it.quantity || 0)).toFixed(2)}
                </Typography>
              </Stack>
            ))}
            {items.length > 6 && (
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                +{items.length - 6} more
              </Typography>
            )}
          </Stack>
          {total > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mt: 0.5,
                fontWeight: 600,
                fontSize: '0.74rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              Total {currency} {total.toFixed(2)}
            </Typography>
          )}
          {o.text && <Box sx={{ mt: 0.5 }}>{renderText(o.text)}</Box>}
        </Panel>
      );
    }

    case 'system':
      return (
        <Panel icon={<InfoOutlined fontSize="small" />} dense>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.74rem' }}>
            {props.system?.body || content}
          </Typography>
          {props.system?.waId && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'text.disabled',
                fontSize: '0.68rem',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              New number: {props.system.waId}
            </Typography>
          )}
        </Panel>
      );

    case 'unknown':
      return (
        <Panel icon={<ErrorOutlineOutlined fontSize="small" />} dense>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.74rem' }}>
            Unsupported message type
          </Typography>
          {props.providerErrors?.[0]?.title && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.disabled', fontSize: '0.68rem' }}
            >
              {props.providerErrors[0].title}
            </Typography>
          )}
        </Panel>
      );

    case 'interactive': {
      const interactive = props.interactive;
      // Absent on an inbound reply-selection (list_reply/button_reply) or on
      // any message predating this field — both render as their synthesised
      // content string, same as before this case existed.
      if (!interactive) return <>{renderText(content)}</>;

      if (interactive.kind === 'list') {
        const rows = (interactive.sections ?? []).flatMap((s) => s.rows ?? []);
        return (
          <Panel icon={<FormatListBulletedOutlined fontSize="small" />}>
            {interactive.headerText && (
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
                {interactive.headerText}
              </Typography>
            )}
            <Box sx={{ mt: interactive.headerText ? 0.25 : 0 }}>
              {renderText(interactive.bodyText || content)}
            </Box>
            {interactive.footerText && (
              <Typography
                variant="caption"
                sx={{ display: 'block', color: 'text.secondary', fontSize: '0.72rem', mt: 0.25 }}
              >
                {interactive.footerText}
              </Typography>
            )}
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.75 }}>
              <Chip size="small" label={interactive.buttonText || 'Menu'} sx={{ fontSize: '0.7rem', height: 22 }} />
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.68rem' }}>
                {rows.length} option{rows.length === 1 ? '' : 's'}
              </Typography>
            </Stack>
            <Stack spacing={0.5} sx={{ mt: 0.5 }}>
              {rows.slice(0, 6).map((row, i) => (
                <Box key={row.id ?? i} sx={{ borderTop: i === 0 ? undefined : `1px solid ${BORDER}`, pt: i === 0 ? 0 : 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.74rem', display: 'block' }}>
                    {row.title}
                  </Typography>
                  {row.description && (
                    <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.68rem' }}>
                      {row.description}
                    </Typography>
                  )}
                </Box>
              ))}
              {rows.length > 6 && (
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.66rem' }}>
                  +{rows.length - 6} more
                </Typography>
              )}
            </Stack>
          </Panel>
        );
      }

      // kind === 'button'
      const header = interactive.header;
      return (
        <Panel icon={<SmartButtonOutlined fontSize="small" />}>
          {header?.type === 'text' && header.text && (
            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.82rem' }}>
              {header.text}
            </Typography>
          )}
          {header && header.type !== 'text' && (
            <Typography
              variant="caption"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                color: 'text.secondary',
                fontSize: '0.72rem',
                mb: 0.25,
              }}
            >
              <DescriptionOutlined sx={{ fontSize: 13 }} />
              {header.filename || `${header.type} attached`}
            </Typography>
          )}
          <Box>{renderText(interactive.bodyText || content)}</Box>
          {interactive.footerText && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'text.secondary', fontSize: '0.72rem', mt: 0.25 }}
            >
              {interactive.footerText}
            </Typography>
          )}
          <Stack spacing={0.5} sx={{ mt: 0.75, alignItems: 'flex-start' }}>
            {(interactive.buttons ?? []).map((b, i) => (
              <Chip key={b.id ?? i} size="small" label={b.title} sx={{ fontSize: '0.7rem', height: 22 }} />
            ))}
          </Stack>
        </Panel>
      );
    }

    // Instagram-only (Phase 2.3, VERIFIED shapes) — card treatment added
    // 2026-09-28 in the post-Phase-2 UI-wiring audit; the backend has
    // recorded these since Phase 2.3 but the data reached no UI until now.
    case 'story_reply': {
      const sc = props.storyContext;
      const isMention = Boolean(sc?.storyUrl && !sc?.replyToMid);
      return (
        <Panel icon={<AutoStoriesOutlined fontSize="small" />} dense>
          <Typography
            variant="caption"
            sx={{ display: 'block', color: 'text.secondary', fontSize: '0.7rem', mb: content ? 0.25 : 0 }}
          >
            {isMention ? 'Mentioned you in their story' : 'Replied to your story'}
          </Typography>
          {content && renderText(content)}
        </Panel>
      );
    }

    // Deliberately NOT 'unsupported' — that string is already WhatsApp's own
    // content-less-multi-send-stub sentinel, filtered from the transcript
    // entirely (see ChatLogs.tsx's NON_TRANSCRIPT_TYPES). Instagram's own
    // `message.is_unsupported` signal is a real, visible customer event, so
    // the normalizer emits this distinct string instead of colliding with it.
    case 'instagram_unsupported':
      return (
        <Panel icon={<ErrorOutlineOutlined fontSize="small" />} dense>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.74rem' }}>
            Instagram could not deliver this message's content
          </Typography>
        </Panel>
      );

    case 'unsent':
      return (
        <Panel icon={<DoNotDisturbOnOutlined fontSize="small" />} dense>
          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '0.74rem' }}>
            {content || 'Message unsent'}
          </Typography>
        </Panel>
      );

    // A tap on an ice-breaker question or a persistent-menu item (Phase 2.3
    // /2.6, VERIFIED shape) — a real customer action, unlike a reaction, so
    // it renders as its own bubble rather than decorating an earlier message.
    case 'postback':
      return (
        <Panel icon={<SmartButtonOutlined fontSize="small" />} dense>
          {renderText(content)}
        </Panel>
      );

    default: {
      // text, button, and any type added server-side before the client knows
      // about it. `content` is always a sensible display string.
      //
      // Instagram quick-reply tap: still plain text (messageType stays
      // 'text'), but decorated with a small "replied via quick reply" tag so
      // it doesn't read as an unprompted message the customer typed from
      // scratch — VERIFIED shape (message.quick_reply.payload, Phase 2.3).
      if (props.quickReplyPayload) {
        return (
          <Box>
            {renderText(content)}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.5 }}>
              <ReplyOutlined sx={{ fontSize: 12, color: 'text.disabled' }} />
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.66rem' }}>
                Quick reply
              </Typography>
            </Stack>
          </Box>
        );
      }
      return <>{renderText(content)}</>;
    }
  }
}

/**
 * Emoji reactions others placed on a message, shown as a small badge row under
 * the bubble. The emoji here IS the content — it is the customer's literal
 * reaction — which is why this is the one place emoji are correct.
 */
export function MessageReactions({
  reactions,
}: {
  reactions?: Array<{ by: string; emoji: string }> | undefined;
}) {
  if (!reactions?.length) return null;
  return (
    <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
      {reactions.slice(0, 4).map((r, i) => (
        <Chip
          key={i}
          size="small"
          label={r.emoji}
          title={`Reacted by ${r.by}`}
          sx={{
            height: 20,
            fontSize: '0.78rem',
            bgcolor: 'background.paper',
            border: `1px solid ${BORDER}`,
            '& .MuiChip-label': { px: 0.75 },
          }}
        />
      ))}
      {reactions.length > 4 && (
        <Typography
          variant="caption"
          sx={{ color: 'text.disabled', fontSize: '0.68rem', alignSelf: 'center' }}
        >
          +{reactions.length - 4}
        </Typography>
      )}
    </Stack>
  );
}
